/**
 * Launcher flyouts — a surface whose manifest declares `menuOp` gets a
 * submenu under its Surfaces-launcher row: the shell runs the op once per
 * boot (queue-native; scoped to the verified learner server-side) and
 * renders each returned item; picking one opens the surface with
 * target.params.menu set to the item id. Any failure degrades to the plain
 * row — the surface works exactly as before, it just has no flyout (e.g.
 * its data worker predates the op and answers "unknown operation").
 */
import { createSurfaceRuntime } from './surfaceRuntime'
import { createQueueQuery } from './queueQuery'

export interface SurfaceMenuItem {
  id: string
  label: string
  /** Two-sided surfaces: the faces THIS learner may use for THIS item (a
   *  subset of the surface's declared faces — a connections pool you lead
   *  carries ['participant','leader'], one you only belong to carries
   *  ['participant']). Absent = the surface's default face only, no flip —
   *  conservative by contract so an older data worker never over-offers. */
  faces?: string[]
}

export async function fetchSurfaceMenu(surfaceId: string, op: string): Promise<SurfaceMenuItem[]> {
  const rt = createSurfaceRuntime(surfaceId)
  const { query } = createQueueQuery(surfaceId, rt)
  const body = await query<{ items?: unknown }>(op)
  const items = Array.isArray(body?.items) ? body.items : []
  return items
    .filter(
      (x): x is { id: string; label: string; faces?: unknown } =>
        !!x &&
        typeof (x as { id?: unknown }).id === 'string' &&
        typeof (x as { label?: unknown }).label === 'string',
    )
    .map((x) => ({
      id: x.id,
      label: x.label,
      ...(Array.isArray(x.faces) && x.faces.every((f) => typeof f === 'string')
        ? { faces: x.faces as string[] }
        : {}),
    }))
}
